import { useRef, useState, type ReactNode } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Alert, Button, Card, Select, Spin } from 'antd'
import { ApiRequestError } from '@/lib/httpClient'
import { useAuthSession } from '@/lib/session'
import AuthLogo from '@/features/auth/components/AuthLogo'
import type { McpAuthorizationDecision } from '@/api/mcpAuthorize'
import { useAnswerMcpAuthorizationMutation, useMcpAuthorization } from '../hooks'

const GUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/**
 * The API builds the redirect, but it goes to `location.assign`: only a web
 * address, and only to the host the admin was shown before they clicked.
 */
function isExpectedRedirect(url: string, expectedHost: string): boolean {
  if (!expectedHost) return false
  try {
    const { protocol, hostname } = new URL(url)
    if (hostname.toLowerCase() !== expectedHost.toLowerCase()) return false
    // Mirrors the server: https anywhere it allows, plain http only back to this machine (Claude Desktop).
    return protocol === 'https:' || (protocol === 'http:' && LOOPBACK_HOSTS.has(hostname.toLowerCase()))
  } catch {
    return false
  }
}

const LOOPBACK_HOSTS = new Set(['localhost', '127.0.0.1', '[::1]'])

/**
 * Inside another page's frame, the click could be tricked out of the admin
 * (clickjacking). The frame-ancestors header blocks that by default; this holds
 * even where an environment widens it. Cypress runs every app in a frame, so it
 * is let through — but never in a production build, where `window.Cypress` could
 * also be an element named "Cypress".
 */
function isFramed(): boolean {
  if (import.meta.env.MODE !== 'production' && 'Cypress' in window) return false
  try {
    return window.self !== window.top
  } catch {
    return true
  }
}

function answerErrorMessage(error: unknown): string {
  if (error instanceof ApiRequestError && (error.status === 400 || error.status === 403)) {
    const message = error.message.trim()
    if (message && message !== 'Request failed') return message
  }
  return 'Something went wrong. Try again in a moment.'
}

function Frame({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-[#d1e8e8] p-6">
      <AuthLogo className="mb-6 h-14 w-auto" />
      <Card className="w-full max-w-md">
        <h1 className="mb-3 text-xl font-semibold text-gray-900">{title}</h1>
        {children}
      </Card>
    </div>
  )
}

export default function McpAuthorizePage() {
  const navigate = useNavigate()
  const session = useAuthSession()
  const [searchParams] = useSearchParams()
  const raw = searchParams.get('request')?.trim() ?? ''
  const requestId = GUID.test(raw) ? raw : ''
  const [framed] = useState(isFramed)

  const query = useMcpAuthorization(framed ? '' : requestId)
  const { mutate: answer, isPending: answering } = useAnswerMcpAuthorizationMutation(requestId)
  // `final` when retrying cannot help: expired, already answered, or not an admin.
  const [answerError, setAnswerError] = useState<{ message: string; final: boolean } | null>(null)
  const [leaving, setLeaving] = useState(false)
  const [chosen, setChosen] = useState<McpAuthorizationDecision | null>(null)
  const [pickedClientId, setPickedClientId] = useState('')
  // The button's disabled state lags a render behind a double click; this does not.
  const answered = useRef(false)

  const backButton = (
    <Button onClick={() => navigate('/', { replace: true })}>Back to DashboardInsights</Button>
  )

  const decide = (decision: McpAuthorizationDecision, expectedHost: string, clientId?: string) => {
    if (answered.current) return
    answered.current = true
    setChosen(decision)
    setAnswerError(null)
    answer({ decision, clientId }, {
      onSuccess: (redirectUrl) => {
        if (!isExpectedRedirect(redirectUrl, expectedHost)) {
          setAnswerError({
            message: `DashboardInsights did not send you back to ${expectedHost || 'the app that asked'}, so you have not been redirected. Start again from your AI assistant.`,
            final: true,
          })
          return
        }
        setLeaving(true)
        window.location.assign(redirectUrl)
      },
      onError: (error) => {
        const final = error instanceof ApiRequestError && (error.status === 400 || error.status === 403)
        if (!final) answered.current = false
        setAnswerError({ message: answerErrorMessage(error), final })
      },
    })
  }

  const notFound = (
    <Frame title="Sign-in not found">
      <p className="mb-4 text-gray-600">This sign-in wasn&rsquo;t found. Start again from your AI assistant.</p>
      {backButton}
    </Frame>
  )

  if (framed) {
    return (
      <Frame title="Open this in its own tab">
        <p className="text-gray-600">
          For your security, this sign-in can&rsquo;t be used inside another page. Open the link in its own
          browser tab to continue.
        </p>
      </Frame>
    )
  }

  if (!requestId) return notFound

  if (query.isLoading) {
    return (
      <Frame title="Connect to DashboardInsights">
        <div className="flex justify-center py-6">
          <Spin />
        </div>
      </Frame>
    )
  }

  if (query.isError) {
    if (query.error instanceof ApiRequestError && query.error.status === 404) return notFound
    return (
      <Frame title="Connect to DashboardInsights">
        <p className="mb-4 text-gray-600">Could not load this sign-in. Try again in a moment.</p>
        <div className="flex flex-wrap gap-2">
          <Button type="primary" onClick={() => query.refetch()}>
            Try again
          </Button>
          {backButton}
        </div>
      </Frame>
    )
  }

  const request = query.data
  if (!request || request.status === 'unknown') return notFound

  if (answerError) {
    return (
      <Frame title="Connect to DashboardInsights">
        <Alert type="error" showIcon message={answerError.message} className="mb-4" />
        <div className="flex flex-wrap gap-2">
          {!answerError.final && (
            <Button type="primary" onClick={() => setAnswerError(null)}>
              Try again
            </Button>
          )}
          {backButton}
        </div>
      </Frame>
    )
  }

  if (request.status === 'expired') {
    return (
      <Frame title="Sign-in expired">
        <p className="mb-4 text-gray-600">This sign-in has expired. Start again from your AI assistant.</p>
        {backButton}
      </Frame>
    )
  }

  if (request.status === 'approved' || request.status === 'denied') {
    return (
      <Frame title="Already answered">
        <p className="mb-4 text-gray-600">
          This sign-in was already answered. Start again from your AI assistant if you need to.
        </p>
        {backButton}
      </Frame>
    )
  }

  const busy = answering || leaving
  // The API's `clientName` is the OAuth client asking (Claude), not a DashboardInsights client.
  const appName = request.clientName || 'An app'
  // The API names a local callback "An app on this computer", which reads lowercase mid-sentence.
  const appNameInSentence = request.clientName.replace(/^An? /, (article) => article.toLowerCase())
  const redirectHost = request.redirectHost || 'the app that asked'
  const yourName = session?.currentUsername || session?.email || 'you'
  const isClientServer = request.server === 'client'
  const clients = request.clients

  if (!request.canApprove || request.server === 'unknown' || (isClientServer && clients.length === 0)) {
    return (
      <Frame title="Connect to DashboardInsights">
        <p className="mb-4 text-gray-600">
          {request.server === 'unknown' ? (
            <>
              This sign-in asks for access DashboardInsights doesn&rsquo;t recognise, so it can&rsquo;t be allowed.
              Deny it, and start again from your AI assistant.
            </>
          ) : isClientServer ? (
            <>You don&rsquo;t have MCP access to any client. Ask your admin to give you access.</>
          ) : (
            <>
              Your account doesn&rsquo;t have access to connect this app. Ask your DashboardInsights administrator
              if you think you should.
            </>
          )}
        </p>
        <Button loading={busy} disabled={busy} onClick={() => decide('deny', request.redirectHost)}>
          Deny
        </Button>
      </Frame>
    )
  }

  // Someone else's sign-in, forwarded here: allowing it would connect their app, not the admin's.
  if (!request.startedInThisBrowser) {
    return (
      <Frame title="Start again from your AI assistant">
        <p className="mb-4 text-gray-600">
          This sign-in was started in a different browser. Start again from your AI assistant, and finish in the browser it
          opens.
        </p>
        <Button loading={busy} disabled={busy} onClick={() => decide('deny', request.redirectHost)}>
          Deny
        </Button>
      </Frame>
    )
  }

  const selectedClient = clients.length === 1 ? clients[0] : clients.find((c) => c.clientId === pickedClientId)

  return (
    <Frame title={appNameInSentence ? `Allow ${appNameInSentence}?` : 'Allow access?'}>
      {isClientServer ? (
        <>
          <p className="mb-2 text-gray-800">
            <b>
              {appName} wants to use DashboardInsights for {selectedClient?.name ?? 'one of your clients'} as{' '}
              {yourName}.
            </b>
          </p>
          <p className="mb-2 text-gray-600">
            It can use your DashboardInsights products only as far as you can in DashboardInsights.
          </p>
          <p className="mb-2 text-gray-600">
            It can make a few changes, such as adding findings; it&rsquo;s told to check with you first.
          </p>
          {clients.length > 1 && (
            <div className="mt-4 mb-4">
              <label htmlFor="mcp-authorize-client" className="mb-2 block text-gray-800">
                Connect {appNameInSentence || 'this app'} to
              </label>
              <Select
                id="mcp-authorize-client"
                className="w-full"
                placeholder="Select a client"
                value={selectedClient?.clientId}
                onChange={setPickedClientId}
                disabled={busy}
                options={clients.map((client) => ({ value: client.clientId, label: client.name }))}
              />
            </div>
          )}
        </>
      ) : (
        <>
          <p className="mb-2 text-gray-800">
            <b>
              {appName} wants to read AdvicePrep support tickets and help articles as {yourName}.
            </b>
          </p>
          <p className="mb-2 text-gray-600">It can&rsquo;t reply to tickets or change anything.</p>
        </>
      )}
      <p className="mb-5 text-gray-600">
        You&rsquo;ll be sent back to <b>{redirectHost}</b>.
      </p>
      {leaving && (
        <div className="mb-4 flex items-center gap-2 text-gray-500">
          <Spin size="small" /> Returning you to {redirectHost}…
        </div>
      )}
      <div className="flex flex-wrap gap-2">
        <Button
          type="primary"
          loading={busy && chosen === 'approve'}
          disabled={busy || (isClientServer && !selectedClient)}
          onClick={() => decide('approve', request.redirectHost, selectedClient?.clientId)}
        >
          {appNameInSentence ? `Allow ${appNameInSentence}` : 'Allow'}
        </Button>
        <Button
          loading={busy && chosen === 'deny'}
          disabled={busy}
          onClick={() => decide('deny', request.redirectHost)}
        >
          Deny
        </Button>
      </div>
    </Frame>
  )
}
